/*
  native-swatch.js — Swatch King replacement (Impulse 9.x adapter).
  Loaded with defer right BEFORE theme.js. Impulse initialises sections while theme.js
  executes, so we hook theme.Variants / theme.VariantAvailability / theme.Product with a
  setter and patch each prototype the moment theme.js defines it. theme.js itself is untouched.
  Only containers that hold [data-native-swatch-picker] get the new behaviour.
  Docs: docs/NATIVE-SWATCHES-CART.md
*/
(function () {
  'use strict';

  var cfg = window.nativeSwatch || {};
  var strings = cfg.strings || {};

  // Run patch(klass) as soon as theme.js assigns theme[name] (or now, if it already exists)
  function hook(name, patch) {
    window.theme = window.theme || {};
    var apply = function (klass) {
      if (klass && klass.prototype && !klass.__nativeSwatchPatched) {
        klass.__nativeSwatchPatched = true;
        patch(klass);
      }
    };
    if (theme[name]) {
      apply(theme[name]);
      return;
    }
    var value;
    Object.defineProperty(theme, name, {
      configurable: true,
      enumerable: true,
      get: function () { return value; },
      set: function (klass) { value = klass; apply(klass); }
    });
  }

  function isNative(container) {
    return !!(container && container.querySelector('[data-native-swatch-picker]'));
  }

  function requiresSelection(container) {
    return !!(container && container.querySelector('[data-native-swatch-require-selection]'));
  }

  function groups(container) {
    return container.querySelectorAll('[data-native-swatch-picker] .variant-input-wrap');
  }

  // Names of option groups without a checked value, in display order
  function missingOptions(container) {
    var missing = [];
    groups(container).forEach(function (group) {
      if (!group.querySelector('input[data-variant-input]:checked')) {
        missing.push(group.getAttribute('name'));
      }
    });
    return missing;
  }

  function promptFor(optionName) {
    return (strings.selectOption || 'Select a [option]').replace('[option]', String(optionName).toLowerCase());
  }

  function updateLabels(container) {
    container.querySelectorAll('[data-native-swatch-option]').forEach(function (option) {
      var valueEl = option.querySelector('[data-native-swatch-value]');
      if (!valueEl) return;
      var checked = option.querySelector('input[data-variant-input]:checked');
      if (checked) {
        valueEl.classList.remove('is-error');
        option.classList.remove('native-swatch-option--attention');
        // Like Swatch King: only the color option shows the chosen value ("Couleur — Gris foncé")
        valueEl.textContent = option.classList.contains('native-swatch-option--color') ? ' — ' + checked.value : '';
      } else if (!valueEl.classList.contains('is-error')) {
        valueEl.textContent = '';
      }
    });
  }

  function cartButton(container) {
    return {
      btn: container.querySelector('[data-add-to-cart]'),
      text: container.querySelector('[data-add-to-cart-text]')
    };
  }

  function setSelectState(container, optionName) {
    var el = cartButton(container);
    if (!el.btn || !el.text) return;
    // Not `disabled`: the button must stay focusable/clickable so we can point to the missing option
    el.btn.disabled = false;
    el.btn.classList.remove('disabled');
    el.btn.classList.add('native-swatch-atc--select');
    el.btn.setAttribute('aria-disabled', 'true');
    el.text.textContent = promptFor(optionName);
  }

  function clearSelectState(container) {
    var el = cartButton(container);
    if (!el.btn) return;
    el.btn.classList.remove('native-swatch-atc--select');
    el.btn.removeAttribute('aria-disabled');
  }

  function announce(container, message) {
    var live = container.querySelector('[data-native-swatch-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = message; }, 50);
  }

  // Block add-to-cart while an option is missing; highlight the first missing group instead.
  // Guards both the click (cart apps hook the button directly) and the form submit (Enter key, Impulse).
  function guardAddToCart(evt, btn) {
    if (!btn || !btn.classList.contains('native-swatch-atc--select')) return;

    var container = btn.parentElement;
    while (container && !container.querySelector('[data-native-swatch-picker]')) container = container.parentElement;
    if (!container) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    var missing = missingOptions(container);
    if (!missing.length) return;
    var group = Array.prototype.find.call(groups(container), function (g) {
      return g.getAttribute('name') === missing[0];
    });
    if (group) {
      var wrapper = group.closest('[data-native-swatch-option]');
      var valueEl = wrapper.querySelector('[data-native-swatch-value]');
      if (valueEl) {
        valueEl.classList.add('is-error');
        valueEl.textContent = ' — ' + valueEl.dataset.prompt;
      }
      wrapper.classList.remove('native-swatch-option--attention');
      void wrapper.offsetWidth; // restart animation
      wrapper.classList.add('native-swatch-option--attention');
      wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Move focus only for keyboard users (click from Enter/Space has detail 0); avoids a focus ring for mouse/touch
      var first = group.querySelector('input[data-variant-input]');
      if (first && evt.type === 'click' && evt.detail === 0) first.focus({ preventScroll: true });
    }
    announce(container, promptFor(missing[0]));
  }

  window.addEventListener('click', function (evt) {
    var btn = evt.target && evt.target.closest ? evt.target.closest('[data-add-to-cart]') : null;
    guardAddToCart(evt, btn);
  }, true);

  window.addEventListener('submit', function (evt) {
    var form = evt.target;
    guardAddToCart(evt, form && form.querySelector ? form.querySelector('[data-add-to-cart]') : null);
  }, true);

  /* ---------- theme.Variants patch ---------- */
  hook('Variants', function (Variants) {
    var VP = Variants.prototype;
    var originalOnSelectChange = VP._onSelectChange;

    // Variant used for image/price preview while the selection is incomplete
    VP._nativePreviewVariant = function (selected) {
      var matches = this.variants.filter(function (v) {
        return selected.every(function (s) { return v[s.index] === s.value; });
      });
      return matches.find(function (v) { return v.available && v.featured_image; }) ||
        matches.find(function (v) { return v.featured_image; }) ||
        matches[0] || null;
    };

    // Native pickers always use exact matching (sold out stays selectable, no jumping to
    // another variant), with or without preselection.
    VP._onSelectChange = function (evt) {
      if (!isNative(this.container)) {
        originalOnSelectChange.call(this, evt);
        return;
      }

      var src = evt.srcElement || evt.target;
      var missing = missingOptions(this.container);
      var selected = this.currentlySelectedValues = this._getCurrentOptions();
      // Exact match only — never jump to another (available) variant, sold out stays selectable
      var variant = missing.length ? null : (this._getFullMatch(false) || null);

      var detail = {
        variant: variant,
        currentlySelectedValues: selected,
        value: src.value,
        index: src.parentElement.dataset.index,
        nativeMissing: missing
      };

      this.container.dispatchEvent(new CustomEvent('variantChange', { detail: detail }));
      document.dispatchEvent(new CustomEvent('variant:change', { detail: detail }));
      updateLabels(this.container);

      var preview = variant || this._nativePreviewVariant(selected);
      if (preview) {
        this._updateImages(preview);
        this._updatePrice(preview);
        this._updateUnitPrice(preview);
      }

      if (!variant) return;

      this._updateMasterSelect(variant);
      this._updateSKU(variant);
      this.currentVariant = variant;
      if (this.enableHistoryState) this._updateHistoryState(variant);
    };
  });

  /* ---------- theme.VariantAvailability patch ---------- */
  hook('VariantAvailability', function (VariantAvailability) {
    var AP = VariantAvailability.prototype;
    var originalTree = AP.createAvailableOptionsTree;
    var originalInitial = AP.setInitialAvailability;
    var originalEnable = AP.enableVariantOption;

    // A value is crossed out when no available variant has it in combination with the
    // other currently selected options. Works with partial selections.
    AP.createAvailableOptionsTree = function (variants, selected) {
      if (!isNative(this.container)) return originalTree.apply(this, arguments);
      var tree = { option1: [], option2: [], option3: [] };
      variants.forEach(function (v) {
        Object.keys(tree).forEach(function (idx) {
          if (v[idx] === null || v[idx] === undefined) return;
          var entry = tree[idx].find(function (o) { return o.value === v[idx]; });
          if (!entry) {
            entry = { value: v[idx], soldOut: true };
            tree[idx].push(entry);
          }
          if (!entry.soldOut || !v.available) return;
          var matchesOthers = selected.every(function (s) {
            return s.index === idx || v[s.index] === s.value;
          });
          if (matchesOthers) entry.soldOut = false;
        });
      });
      return tree;
    };

    AP.setInitialAvailability = function () {
      if (!isNative(this.container)) return originalInitial.apply(this, arguments);
      var selected = [];
      this.container.querySelectorAll('[data-native-swatch-picker] input[data-variant-input]:checked').forEach(function (el) {
        selected.push({ value: el.value, index: el.dataset.index });
      });
      var tree = this.createAvailableOptionsTree(this.variantsObject, selected);
      for (var option in tree) {
        if (this.container.querySelector('.variant-input-wrap[data-index="' + option + '"]')) {
          this.manageOptionState(option, tree[option]);
        }
      }
    };

    // Keep the screen-reader "sold out" suffix in sync with the crossed-out state
    AP.enableVariantOption = function (group, obj) {
      originalEnable.apply(this, arguments);
      if (!group || !isNative(this.container) || this.type === 'dropdown') return;
      var value = obj.value.replace(/([ #;&,.+*~\':"!^$[\]()=>|\\\/@])/g, '\\$1');
      var holder = group.querySelector('.variant-input[data-value="' + value + '"] [data-native-swatch-soldout-text]');
      if (holder) holder.textContent = obj.soldOut ? ', ' + (strings.soldOut || 'Sold out') : '';
    };
  });

  /* ---------- theme.Product patch ---------- */
  hook('Product', function (Product) {
    var PP = Product.prototype;
    var originalUpdateCartButton = PP.updateCartButton;
    var originalInitVariants = PP.initVariants;

    PP.updateCartButton = function (evt) {
      var detail = (evt && evt.detail) || {};
      if (detail.nativeMissing && detail.nativeMissing.length) {
        setSelectState(this.container, detail.nativeMissing[0]);
        return;
      }
      clearSelectState(this.container);
      originalUpdateCartButton.call(this, evt);
    };

    PP.initVariants = function () {
      originalInitVariants.apply(this, arguments);
      var container = this.container;
      if (!isNative(container)) return;

      // With preselection every option is checked, so nothing is missing here
      var missing = missingOptions(container);
      if (missing.length) setSelectState(container, missing[0]);
      applyUrlSwatch(container);
    };
  });

  // ?swatch=<color value> (from product cards) checks that color only
  function applyUrlSwatch(container) {
    var wanted;
    try {
      wanted = new URLSearchParams(window.location.search).get('swatch');
    } catch (e) {
      return;
    }
    if (!wanted) return;
    var input = Array.prototype.find.call(
      container.querySelectorAll('[data-native-swatch-picker] .native-swatch-group--color input[data-variant-input]'),
      function (el) { return el.value === wanted; }
    );
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* ---------- Product cards: hover/tap swaps the card image ---------- */
  function cardImage(card) {
    var imgs = card.querySelectorAll('.grid-product__image-mask img');
    for (var i = 0; i < imgs.length; i++) {
      if (!imgs[i].closest('.grid-product__secondary-image')) return imgs[i];
    }
    return null;
  }

  function showSwatchImage(swatch) {
    var card = swatch.closest('.grid-product');
    var img = card && cardImage(card);
    if (!img || !swatch.dataset.nativeSwatchImage) return;
    if (!img.dataset.nativeOriginalSrc) {
      img.dataset.nativeOriginalSrc = img.getAttribute('src') || '';
      img.dataset.nativeOriginalSrcset = img.getAttribute('srcset') || '';
    }
    img.setAttribute('srcset', swatch.dataset.nativeSwatchSrcset || '');
    img.setAttribute('src', swatch.dataset.nativeSwatchImage);
    card.classList.add('native-swatch-card--previewing');
    card.querySelectorAll('.native-swatch-card__swatch').forEach(function (s) {
      s.classList.toggle('is-active', s === swatch);
    });
  }

  function restoreCardImage(card) {
    var img = cardImage(card);
    if (img && img.dataset.nativeOriginalSrc !== undefined && !card.dataset.nativeSwatchLocked) {
      img.setAttribute('srcset', img.dataset.nativeOriginalSrcset);
      img.setAttribute('src', img.dataset.nativeOriginalSrc);
      card.classList.remove('native-swatch-card--previewing');
      card.querySelectorAll('.native-swatch-card__swatch.is-active').forEach(function (s) {
        s.classList.remove('is-active');
      });
    }
  }

  function swatchFrom(target) {
    return target && target.closest ? target.closest('.native-swatch-card__swatch') : null;
  }

  if (cfg.cardHover !== false) {
    var lastPointer = 'mouse';
    document.addEventListener('pointerdown', function (evt) { lastPointer = evt.pointerType; }, true);

    document.addEventListener('mouseover', function (evt) {
      var swatch = swatchFrom(evt.target);
      if (swatch) showSwatchImage(swatch);
    });
    document.addEventListener('focusin', function (evt) {
      var swatch = swatchFrom(evt.target);
      if (swatch) showSwatchImage(swatch);
    });
    document.addEventListener('mouseout', function (evt) {
      var list = evt.target.closest && evt.target.closest('[data-native-swatch-card]');
      if (!list || list.contains(evt.relatedTarget)) return;
      restoreCardImage(list.closest('.grid-product'));
    });

    // Touch: first tap previews the color (and points the card links at it), second tap opens it
    document.addEventListener('click', function (evt) {
      var swatch = swatchFrom(evt.target);
      if (!swatch || lastPointer !== 'touch' || swatch.dataset.nativeTapped) return;
      evt.preventDefault();
      var card = swatch.closest('.grid-product');
      card.querySelectorAll('.native-swatch-card__swatch').forEach(function (s) { delete s.dataset.nativeTapped; });
      swatch.dataset.nativeTapped = '1';
      card.dataset.nativeSwatchLocked = '1';
      showSwatchImage(swatch);
      card.querySelectorAll('a.grid-product__link, .grid-product__image-mask a').forEach(function (a) {
        a.setAttribute('href', swatch.getAttribute('href'));
      });
    });
  }
})();
